import { ChangeDetectorRef, Component, OnInit, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { SubscriptionComponent } from '../../../../../shared/helpers/subscription.component';
import { fb, isAllowed, router } from '../../../../../shared/shared.global-service';
import { Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BreadcrumbService } from 'xng-breadcrumb';
import { BreadcrumbsEnum } from '../../../../../shared/models/breadcrumbs.enum';
import { isInvalid } from '../../../../../shared/helpers/validate';
import { ServiceNursingDiagnosisDetailPageStore } from './service-nursing-diagnosis-detail-page.store';
import {
    TextPromptDialogComponent,
} from '../../../../components/services/dialogs/text-prompt-dialog/text-prompt-dialog.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FilterBuilder } from '../../../../helper/filter.builder';
import {
    ServiceNursingCareDiagnosisGroupDialogComponent,
} from '../../../../components/services/dialogs/service-nursing-care-diagnosis-group-dialog/service-nursing-care-diagnosis-group-dialog.component';
import {
    ServiceNursingCareDiagnosisGroupObject,
} from '@libs/types/src/services/object-type/service/service-nursing-care-diagnosis-group.object';
import {
    ServiceNursingCareDiagnosisCodeCreateInput,
} from '@libs/types/src/services/input-type/services/service-nursing-care-diagnosis-code.create-input';
import {
    ServiceNursingCareDiagnosisCodeUpdateInput,
} from '@libs/types/src/services/input-type/services/service-nursing-care-diagnosis-code.update-input';
import { BaseDialogService } from '../../../../../../components/kit/base-dialog/base-dialog-service';
import { CardComponent } from '../../../../../../components/kit/card/card.component';
import { TextInputComponent } from '../../../../../../components/kit/input/text-input/text-input.component';
import { SelectInputComponent } from '../../../../../../components/kit/input/select-input/select-input.component';
import { AsyncPipe } from '@angular/common';
import { GreenOrRedDotComponent } from '../../../../../../components/kit/green-or-red-dot/green-or-red-dot.component';
import { ButtonComponent } from '../../../../../../components/kit/button/button.component';
import { LinkButtonComponent } from '../../../../../../components/kit/button/link-button.component';
import { Divider } from '@openng/optimus-ui/divider';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@openng/optimus-ui/tabs';
import { ActionButtonsComponent } from '../../../../../../components/kit/action-buttons/action-buttons.component';
import {
    NursingDiagnosisTabComponent,
} from '../../../../components/services/nursing-diagnosis-tab/nursing-diagnosis-tab.component';

export type ServiceNursingDiagnosisProp = 'factors' | 'interventions' | 'goals' | 'riskFactors' | 'symptoms';

@Component({
    selector: 'app-service-nursing-diagnosis-detail-page',
    templateUrl: './service-nursing-diagnosis-detail-page.component.html',
    styleUrl: './service-nursing-diagnosis-detail-page.component.scss',
    providers: [ServiceNursingDiagnosisDetailPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CardComponent,
        TextInputComponent,
        SelectInputComponent,
        AsyncPipe,
        GreenOrRedDotComponent,
        ButtonComponent,
        LinkButtonComponent,
        Divider,
        Tabs,
        TabList,
        Tab,
        TranslatePipe,
        TabPanels,
        TabPanel,
        ActionButtonsComponent,
        NursingDiagnosisTabComponent,
        ServiceNursingCareDiagnosisGroupDialogComponent,
    ],
})
export class ServiceNursingDiagnosisDetailPageComponent extends SubscriptionComponent implements OnInit {

    protected readonly store = inject(ServiceNursingDiagnosisDetailPageStore);
    protected readonly route = inject(ActivatedRoute);
    private readonly breadcrumbs = inject(BreadcrumbService);
    private readonly dialog = inject(BaseDialogService);
    private readonly translate = inject(TranslateService);
    private readonly cdr = inject(ChangeDetectorRef);

    @ViewChild(ServiceNursingCareDiagnosisGroupDialogComponent) groupDialog!: ServiceNursingCareDiagnosisGroupDialogComponent;

    public props: Record<ServiceNursingDiagnosisProp, any> = {
        factors: [],
        interventions: [],
        goals: [],
        riskFactors: [],
        symptoms: [],
    };

    protected form = fb.group({
        code: fb.control<string>('', Validators.required),
        title: fb.control<string>('', Validators.required),
        groupId: fb.control<number | null>(null, Validators.required),
        description: fb.control<string>(''),
    });

    protected title = 'service.nursingDiagnoses.add.title';
    protected serviceId = 0;

    private diagnosisId: number | null = null;


    constructor() {
        super();
        this.subs.push(
            this.store.routeParam.nursingDiagnosisId.subscribe((it) => {
                this.diagnosisId = Number(it) || null;
                if (this.diagnosisId) {
                    this.store.api.getServiceNursingCareDiagnosisCodes(FilterBuilder.singleItem({ id: this.diagnosisId }));
                    this.title = 'service.nursingDiagnoses.edit.title';
                }
            }),
            this.store.routeParam.serviceId.subscribe((it) => {
                this.serviceId = Number(it) || 0;
                if (this.serviceId) {
                    this.store.api.getServiceNursingCareDiagnosisGroups(FilterBuilder.singlePage({ serviceId: this.serviceId }));
                }
            }),
            this.store.diagnosis$.subscribe((it) => {
                if (it) {
                    this.breadcrumbs.set(`@${BreadcrumbsEnum.ServiceNursingDiagnosis}`, `${BreadcrumbsEnum.ServiceNursingDiagnosis}|${it?.id}`);
                    this.form.patchValue({
                        ...it,
                    });
                    this.props = {
                        factors: [...it.factors.map((f: any) => ({ title: f.title, id: f.id }))],
                        interventions: [...it.interventions.map((f: any) => ({ title: f.title, id: f.id }))],
                        goals: [...it.goals.map((f: any) => ({ title: f.title, id: f.id }))],
                        riskFactors: [...it.riskFactors.map((f: any) => ({ title: f.title, id: f.id }))],
                        symptoms: [...it.symptoms.map((f: any) => ({ title: f.title, id: f.id }))],
                    };
                }
            }),
        );
        this.store.doOnActions(
            () => router.navigate(['../'], { relativeTo: this.route }),
            'createServiceNursingCareDiagnosisCode',
            'updateServiceNursingCareDiagnosisCode',
        );

        this.store.doOnActions(
            () => this.store.api.getServiceNursingCareDiagnosisGroups(FilterBuilder.singlePage({ serviceId: this.serviceId })),
            'createServiceNursingCareDiagnosisGroup',
            'updateServiceNursingCareDiagnosisGroup',
            'deleteServiceNursingCareDiagnosisGroup',
        );

        this.store.registerSuccessMessage('createServiceNursingCareDiagnosisCode', 'common.created');
        this.store.registerSuccessMessage('updateServiceNursingCareDiagnosisCode', 'common.updated');
    }

    ngOnInit(): void {
        if (!isAllowed['service.update']()) {
            this.form.disable();
        }
    }

    async submit(): Promise<void> {
        if (isInvalid(this.form)) {
            return;
        }
        const base = {
            ...this.form.value,
            ...this.props,
        };

        if (this.diagnosisId) {
            const data = base as ServiceNursingCareDiagnosisCodeUpdateInput;
            data.id = this.diagnosisId;
            this.store.api.updateServiceNursingCareDiagnosisCode(data);
        } else {
            const data = base as ServiceNursingCareDiagnosisCodeCreateInput;
            data.serviceId = Number(this.store.routeParam.serviceId.value);
            this.store.api.createServiceNursingCareDiagnosisCode(data);
        }
    }

    protected addToDiagnosis(prop: ServiceNursingDiagnosisProp): void {
        const ref = this.dialog.open(TextPromptDialogComponent, {
            header: this.translate.instant(`service.nursingDiagnoses.tabs.${prop}.addToDiagnosis`),
        });

        this.subs.push(
            ref.onClose.subscribe((it) => {
                if (it) {
                    // because of change detection
                    this.props[prop] = [...this.props[prop], { title: it }];
                    this.cdr.markForCheck();
                }
            }),
        );
    }

    protected removeFromDiagnosis(prop: ServiceNursingDiagnosisProp, item: any): void {
        const index = this.props[prop].indexOf(item);
        this.props[prop].splice(index, 1);
    }

    protected editDiagnosisProp(prop: ServiceNursingDiagnosisProp, item: any): void {
        const index = this.props[prop].indexOf(item);
        const ref = this.dialog.open(TextPromptDialogComponent, {
            header: this.translate.instant(`service.nursingDiagnoses.tabs.${prop}.editDiagnosisProp`),
            data: {
                value: this.props[prop][index].title,
            },
        });

        this.subs.push(
            ref.onClose.subscribe((it) => {
                if (it) {
                    this.props[prop] = this.props[prop].map((propItem: any, idx: number) =>
                        idx === index ? { ...propItem, title: it } : propItem,
                    );
                }
            }),
        );
    }

    createGroup(): void {
        this.groupDialog.show();
    }

    updateGroup(group: ServiceNursingCareDiagnosisGroupObject): void {
        this.groupDialog.show(true, group);
    }
}
